import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CADENCE } from "@/constants/testIds";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Save, Trash2, Timer, MessageCircle, Mail, GripVertical, X, Info, Sparkles, Pencil, Check } from "lucide-react";
import HintIcon from "@/components/HintIcon";
import { HMSField } from "@/components/TimingFields";

// Effective offset seconds for a step — prefers `seconds_after_signup`, falls back to hours*3600
function stepOffsetSeconds(step) {
  const s = step?.seconds_after_signup;
  if (s !== null && s !== undefined) return parseInt(s, 10);
  return (parseInt(step?.hours_after_signup ?? 0, 10) || 0) * 3600;
}

function fmtOffset(seconds) {
  const s = Math.max(0, parseInt(seconds || 0, 10));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (sec) parts.push(`${sec}s`);
  return parts.length ? parts.join(" ") : "0s";
}

const KIND_META = {
  trial_conversion: {
    label: "Trial Conversion",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    hint: "Runs for new trial users during the 0-72h window. Automatically applied on signup.",
  },
  post_subscription: {
    label: "Post-Subscription",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    hint: "Runs after a user subscribes. Use for onboarding, feature discovery, and retention.",
  },
  win_back: {
    label: "Win-back",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    hint: "Runs for users whose trial ended without paying. Use for offers and re-engagement.",
  },
  custom: {
    label: "Custom",
    color: "bg-slate-100 text-slate-700 border-slate-200",
    hint: "A custom cadence — you decide when it applies (via tags or manual assignment).",
  },
};

function SortableStep({ step, idx, templates, offers, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const channels = step.channels || (step.channel ? [step.channel] : []);
  const templatesMap = step.templates || (step.channel && step.template_id ? { [step.channel]: step.template_id } : {});
  const aiDraftChannels = step.ai_draft_channels || [];

  // Derive per-channel static labels from the selected templates
  const stripDay = (s) => (s || "").replace(/^Day\s*\d+\s*[–-]\s*/i, "").trim();
  const templateNameFor = (ch) => {
    const id = templatesMap[ch];
    if (!id) return "";
    return stripDay(templates.find((t) => t.id === id)?.name || "");
  };
  const waLabel = templateNameFor("whatsapp");
  const emLabel = templateNameFor("email");
  // Composite label for backward-compat storage (backend still expects a single string)
  const derivedLabel = waLabel && emLabel ? `${waLabel} / ${emLabel}` : (waLabel || emLabel || "");
  if (derivedLabel && step.label !== derivedLabel) {
    Promise.resolve().then(() => onChange({ label: derivedLabel }));
  }

  const toggleChannel = (ch) => {
    let next = [...channels];
    if (next.includes(ch)) next = next.filter((c) => c !== ch);
    else next.push(ch);
    const nextTemplates = { ...templatesMap };
    if (!next.includes("whatsapp")) delete nextTemplates.whatsapp;
    if (!next.includes("email")) delete nextTemplates.email;
    // Drop AI-draft flag for a channel that's no longer used
    const nextAi = aiDraftChannels.filter((c) => next.includes(c));
    onChange({ channels: next, templates: nextTemplates, ai_draft_channels: nextAi });
  };

  const setTemplate = (ch, id) =>
    onChange({ templates: { ...templatesMap, [ch]: id } });

  const toggleAiDraft = (ch) => {
    const next = aiDraftChannels.includes(ch)
      ? aiDraftChannels.filter((c) => c !== ch)
      : [...aiDraftChannels, ch];
    onChange({ ai_draft_channels: next });
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`${CADENCE.stepPrefix}${idx}`} className="relative pl-14 pb-4 last:pb-0">
      <div className="absolute left-4 top-3 h-5 w-5 rounded-full bg-orange-500 border-4 border-orange-100 flex items-center justify-center">
        <Timer className="h-2.5 w-2.5 text-white" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <button
            {...attributes}
            {...listeners}
            className="mt-6 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Fires after signup</div>
              <HMSField
                compact
                testId={`cadence-step-${idx}-offset`}
                seconds={stepOffsetSeconds(step)}
                onChange={(sec) => onChange({
                  seconds_after_signup: sec,
                  hours_after_signup: Math.floor(sec / 3600),
                })}
              />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Channels</div>
              <div className="flex gap-2 flex-wrap">
                {["whatsapp", "email"].map((ch) => {
                  const on = channels.includes(ch);
                  return (
                    <button
                      key={ch}
                      onClick={() => toggleChannel(ch)}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded border transition-colors duration-150 ${
                        on
                          ? ch === "whatsapp"
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {ch === "whatsapp" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                      {ch}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1.5">
                Templates
                <HintIcon text="Unchecked = send template as-is with {{name}}, {{exam}} etc filled in. Checked = AI drafts a personalized message using the template as a hint (spends tokens)." />
              </div>
              {channels.length === 0 ? (
                <div className="px-2.5 py-2 text-xs italic text-slate-400 border border-dashed border-slate-200 rounded-md bg-slate-50">
                  Pick at least one channel
                </div>
              ) : (
                <div className="space-y-2">
                  {channels.map((ch) => {
                    const aiOn = aiDraftChannels.includes(ch);
                    return (
                      <div key={ch} className="flex items-center gap-2">
                        {ch === "whatsapp"
                          ? <MessageCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                          : <Mail className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                        <select
                          value={templatesMap[ch] || ""}
                          onChange={(e) => setTemplate(ch, e.target.value)}
                          className="flex-1 h-8 px-2 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                        >
                          <option value="">— pick template —</option>
                          {templates.filter((t) => t.channel === ch).map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <label
                          data-testid={`cadence-step-${idx}-ai-draft-${ch}`}
                          title={aiOn
                            ? `AI will draft the ${ch} message using this template as a hint (spends tokens).`
                            : `${ch === "whatsapp" ? "WhatsApp" : "Email"} template will be sent as-is with placeholders filled in.`}
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 h-8 rounded border cursor-pointer transition-colors duration-150 shrink-0 ${
                            aiOn
                              ? "bg-purple-600 text-white border-purple-600"
                              : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={aiOn}
                            onChange={() => toggleAiDraft(ch)}
                          />
                          <Sparkles className="h-3 w-3" />
                          AI
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Label</div>
              {channels.length === 0 ? (
                <div className="h-8 flex items-center px-2.5 text-xs italic text-slate-400 border border-dashed border-slate-200 rounded-md bg-slate-50">
                  —
                </div>
              ) : (
                <div className="space-y-2" data-testid={`cadence-step-${idx}-label`}>
                  {channels.map((ch) => (
                    <div
                      key={ch}
                      className="h-8 flex items-center gap-1.5 px-2.5 text-xs rounded-md bg-slate-50 border border-slate-200 text-slate-700"
                      title={ch === "whatsapp" ? "WhatsApp template name" : "Email template name"}
                    >
                      {ch === "whatsapp"
                        ? <MessageCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                        : <Mail className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                      <span className="truncate">
                        {(ch === "whatsapp" ? waLabel : emLabel)
                          || <span className="text-slate-400 italic">Pick template</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-1 flex justify-end">
              <button
                onClick={onRemove}
                className="mt-6 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-150"
                title="Remove step"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
          <label className="text-xs font-semibold flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={step.active} onChange={(e) => onChange({ active: e.target.checked })} />
            Step active
          </label>
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
            Fires at H+{fmtOffset(stepOffsetSeconds(step))} · {channels.length || 0} channel(s)
          </div>
        </div>
        {/* v28 — Attach offer to this step. When set, sending this step logs
             a grant so the AI is allowed to reveal the code to that user. */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0 flex items-center gap-1">
            Attach offer
            <span title="If set, users who receive this step become eligible for the AI to reveal this code on their reply. Non-granted users can never see it.">
              <Info className="h-3 w-3 text-slate-400" />
            </span>
          </label>
          <select
            data-testid={`cadence-step-offer-${step.id}`}
            value={step.offer_id || ""}
            onChange={(e) => onChange({ offer_id: e.target.value || null })}
            className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="">— none (regular step) —</option>
            {(offers || []).filter((o) => o.active).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code}{o.discount_pct ? ` (${o.discount_pct}%)` : o.discount_amount ? ` (₹${o.discount_amount})` : ""}
                {o.description ? ` — ${o.description.slice(0, 40)}` : ""}
              </option>
            ))}
          </select>
          {step.offer_id && (
            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200 shrink-0">
              Offer step
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Cadence() {
  const [cadences, setCadences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [draftHeader, setDraftHeader] = useState({ name: "", description: "" });
  const [newCadence, setNewCadence] = useState({ name: "", kind: "custom", description: "" });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [offers, setOffers] = useState([]); // v28
  const [groups, setGroups] = useState([]); // v29
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    const [c, t, o, gr] = await Promise.all([
      api.get("/cadences"),
      api.get("/templates"),
      api.get("/offers").catch(() => ({ data: [] })),
      api.get("/groups").catch(() => ({ data: [] })),
    ]);
    // Ensure trial_conversion is first
    const sorted = [...c.data].sort((a, b) => {
      const order = { trial_conversion: 0, post_subscription: 1, win_back: 2, custom: 3 };
      return (order[a.kind] ?? 9) - (order[b.kind] ?? 9);
    });
    setCadences(sorted);
    setTemplates(t.data);
    setOffers(o.data || []);
    setGroups(gr.data || []);
    if (!activeId && sorted.length > 0) setActiveId(sorted[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const active = useMemo(() => cadences.find((c) => c.id === activeId) || cadences[0], [cadences, activeId]);

  const updateActive = (patch) => setCadences((prev) => prev.map((c) => (c.id === active.id ? { ...c, ...patch } : c)));
  const updateStep = (idx, patch) => {
    const steps = [...(active.steps || [])];
    steps[idx] = { ...steps[idx], ...patch };
    updateActive({ steps });
  };
  const removeStep = (idx) => updateActive({ steps: (active.steps || []).filter((_, i) => i !== idx) });
  const addStep = () => updateActive({
    steps: [...(active.steps || []), {
      id: crypto.randomUUID(),
      hours_after_signup: 24, seconds_after_signup: 24 * 3600,
      channels: ["whatsapp"], templates: {}, ai_draft_channels: [],
      label: "New step", active: true,
    }],
  });

  const onDragEnd = (event) => {
    const { active: a, over } = event;
    if (!over || a.id === over.id) return;
    const steps = active.steps || [];
    const oldIndex = steps.findIndex((s) => s.id === a.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    updateActive({ steps: arrayMove(steps, oldIndex, newIndex) });
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const payload = (active.steps || []).map((s) => ({
        id: s.id,
        hours_after_signup: s.hours_after_signup,
        seconds_after_signup: s.seconds_after_signup ?? null,
        channels: s.channels || (s.channel ? [s.channel] : []),
        templates: s.templates || {},
        ai_draft_channels: s.ai_draft_channels || [],
        label: s.label,
        active: s.active,
        offer_id: s.offer_id ?? null,
      }));
      await api.put(`/cadences/${active.id}`, {
        name: active.name,
        kind: active.kind,
        description: active.description,
        steps: payload,
        active: active.active,
        group_id: active.group_id || null,
        override_dnd: !!active.override_dnd,
        timer_anchor: active.timer_anchor || "signup",
      });
      toast.success(`"${active.name}" saved`);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const createCadence = async () => {
    if (!newCadence.name.trim()) return toast.error("Name is required");
    try {
      const { data } = await api.post("/cadences", {
        name: newCadence.name.trim(),
        kind: newCadence.kind,
        description: newCadence.description || "",
        steps: [], active: true,
      });
      toast.success("Cadence created");
      setCreating(false);
      setNewCadence({ name: "", kind: "custom", description: "" });
      await load();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const deleteCadence = async () => {
    if (!active) return;
    if (active.kind === "trial_conversion") return toast.error("The Trial Conversion cadence cannot be deleted");
    // Two-click confirm — native confirm() is blocked by the preview iframe.
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      setTimeout(() => setConfirmingDelete(false), 5000);
      return;
    }
    setConfirmingDelete(false);
    try {
      await api.delete(`/cadences/${active.id}`);
      toast.success("Cadence deleted");
      setActiveId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!active) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  const meta = KIND_META[active.kind] || KIND_META.custom;

  return (
    <div data-testid={CADENCE.root} className="p-8 lg:p-12 max-w-[1100px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Automation</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            Cadences
            <HintIcon
              testId="cadence-page-hint"
              hint={
                <>
                  Design the automated conversation sequences for each lifecycle stage. Users are auto-routed between cadences:<br /><br />
                  <b className="text-orange-700">Trial Conversion</b> — active during the 3-day trial window. Fires the &quot;first push&quot; then Day 1/2/3 steps.<br />
                  <b className="text-indigo-700">Post-Subscription</b> — kicks in when a user pays. Onboarding + retention nudges.<br />
                  <b className="text-slate-700">Win-back</b> — kicks in when the 3-day trial elapses without paying.<br /><br />
                  Each step has an <b>hours_after_signup</b> offset and one or more channels (WhatsApp / Email).
                </>
              }
            />
          </h1>
        </div>
      </div>

      {/* Cadence tabs */}
      <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
        {cadences.map((c) => {
          const m = KIND_META[c.kind] || KIND_META.custom;
          const isActive = c.id === active.id;
          return (
            <button
              key={c.id}
              data-testid={`${CADENCE.tabPrefix}${c.kind}`}
              onClick={() => setActiveId(c.id)}
              className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors duration-150 ${
                isActive ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${isActive ? "bg-orange-400" : "bg-slate-300"}`} />
              {c.name}
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${isActive ? "bg-white/10 border-white/20 text-white" : m.color}`}>
                {(c.steps || []).length} steps
              </span>
            </button>
          );
        })}
        <button
          data-testid={CADENCE.addCadenceBtn}
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-md border border-dashed border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors duration-150"
        >
          <Plus className="h-3.5 w-3.5" /> New cadence
        </button>
      </div>

      {/* Active cadence header */}
      <div className={`mb-4 rounded-md p-3 border flex items-start gap-2 ${meta.color}`}>
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed">
          <b>{meta.label} cadence.</b> {meta.hint}
        </div>
      </div>

      <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4">
        {!editingHeader ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
            <div className="md:col-span-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Cadence name</div>
              <div
                data-testid="cadence-name-static"
                className="w-full px-3 py-2 text-sm rounded-md bg-slate-50 border border-slate-200 text-slate-800 font-medium truncate"
                title={active.name || ""}
              >
                {active.name || <span className="text-slate-400 italic">Unnamed cadence</span>}
              </div>
            </div>
            <div className="md:col-span-6">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Description</div>
              <div
                data-testid="cadence-description-static"
                className="w-full px-3 py-2 text-sm rounded-md bg-slate-50 border border-slate-200 text-slate-700 truncate"
                title={active.description || ""}
              >
                {active.description || <span className="text-slate-400 italic">No description</span>}
              </div>
            </div>
            <div className="md:col-span-2 flex items-end justify-end pt-5 gap-2">
              <label className="text-xs font-semibold flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={active.active} onChange={(e) => updateActive({ active: e.target.checked })} />
                Active
              </label>
              <button
                data-testid="cadence-edit-header-btn"
                onClick={() => { setDraftHeader({ name: active.name || "", description: active.description || "" }); setEditingHeader(true); }}
                className="inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-2.5 py-1.5 rounded-md hover:bg-slate-50 hover:border-slate-300 transition-colors duration-150"
                title="Edit name & description"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
            <div className="md:col-span-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Cadence name</div>
              <input
                data-testid="cadence-name-input"
                value={draftHeader.name}
                onChange={(e) => setDraftHeader((p) => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-orange-300 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
                autoFocus
              />
            </div>
            <div className="md:col-span-6">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Description</div>
              <input
                data-testid="cadence-description-input"
                value={draftHeader.description}
                onChange={(e) => setDraftHeader((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short note visible to admins & agents"
                className="w-full px-3 py-2 text-sm border border-orange-300 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              />
            </div>
            <div className="md:col-span-2 flex items-end justify-end pt-5 gap-2">
              <button
                data-testid="cadence-cancel-header-btn"
                onClick={() => setEditingHeader(false)}
                className="inline-flex items-center gap-1 border border-slate-200 bg-white text-slate-600 text-xs font-semibold px-2.5 py-1.5 rounded-md hover:bg-slate-50"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
              <button
                data-testid="cadence-apply-header-btn"
                onClick={() => { updateActive({ name: draftHeader.name.trim(), description: draftHeader.description.trim() }); setEditingHeader(false); }}
                className="inline-flex items-center gap-1 bg-orange-600 text-white text-xs font-semibold px-2.5 py-1.5 rounded-md hover:bg-orange-500"
              >
                <Check className="h-3 w-3" /> Apply
              </button>
            </div>
          </div>
        )}
        {editingHeader && (
          <div className="mt-2 text-[10.5px] text-slate-500 leading-relaxed">
            Applying is local — click <b>Save cadence</b> to persist changes to the server.
          </div>
        )}
      </div>

      {/* v29.1 — Targeting panel: Group + Override DND + Timer anchor */}
      <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4" data-testid="cadence-targeting-panel">
        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3">Targeting</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
          <div className="md:col-span-5">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 block mb-1">Target group</label>
            <select
              data-testid="cadence-group-select"
              value={active.group_id || ""}
              onChange={(e) => {
                const gid = e.target.value || null;
                // Clearing the group forces timer_anchor back to signup.
                const patch = { group_id: gid };
                if (!gid) patch.timer_anchor = "signup";
                updateActive(patch);
              }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
            >
              <option value="">All users in this lifecycle stage (no group filter)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} {g.auto ? "(auto)" : "(static)"} · {g.member_count ?? 0} members
                </option>
              ))}
            </select>
            <div className="text-[10.5px] text-slate-500 mt-1 leading-relaxed">
              When set, this cadence fires <b>only for members</b> of the chosen group.
            </div>
          </div>

          <div className="md:col-span-4">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 block mb-1">
              Timer anchor
              <span className="ml-1 text-slate-400 normal-case tracking-normal">— what step delays are measured from</span>
            </label>
            <div className="flex flex-col gap-1.5">
              <label className={`flex items-start gap-2 text-xs cursor-pointer p-2 rounded border ${(!active.timer_anchor || active.timer_anchor === "signup") ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  data-testid="cadence-timer-anchor-signup"
                  type="radio"
                  name="timer_anchor"
                  checked={!active.timer_anchor || active.timer_anchor === "signup"}
                  onChange={() => updateActive({ timer_anchor: "signup" })}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-semibold text-slate-800">Signup date <span className="text-slate-500 font-normal">(default)</span></div>
                  <div className="text-[10.5px] text-slate-500 leading-tight">Step delays counted from when the user signed up on PrepHacker.</div>
                </div>
              </label>
              <label
                className={`flex items-start gap-2 text-xs p-2 rounded border ${active.timer_anchor === "group_joined" ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"} ${!active.group_id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                title={!active.group_id ? "Attach a target group first" : ""}
              >
                <input
                  data-testid="cadence-timer-anchor-group"
                  type="radio"
                  name="timer_anchor"
                  disabled={!active.group_id}
                  checked={active.timer_anchor === "group_joined"}
                  onChange={() => updateActive({ timer_anchor: "group_joined" })}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-semibold text-slate-800">Group joined date</div>
                  <div className="text-[10.5px] text-slate-500 leading-tight">Step delays counted from when the user was added to the target group. Requires a group.</div>
                </div>
              </label>
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 block mb-1">DND policy</label>
            <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-slate-200 hover:border-slate-300">
              <input
                data-testid="cadence-override-dnd"
                type="checkbox"
                checked={!!active.override_dnd}
                onChange={(e) => updateActive({ override_dnd: e.target.checked })}
                className="mt-0.5"
              />
              <div>
                <div className="font-semibold text-slate-800">Override DND</div>
                <div className="text-[10.5px] text-slate-500 leading-tight">
                  Fire even for DND / deleted users. Use only with a Meta-approved re-opt-in template.
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            data-testid={CADENCE.addStepBtn}
            onClick={addStep}
            className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150"
          >
            <Plus className="h-3.5 w-3.5" /> Add step
          </button>
          <button
            data-testid={CADENCE.saveBtn}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save cadence"}
          </button>
        </div>
        {active.kind !== "trial_conversion" && (
          <button
            data-testid={CADENCE.deleteCadenceBtn}
            onClick={deleteCadence}
            className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors duration-150 ${confirmingDelete ? "text-white bg-red-600 px-2 py-1 rounded" : "text-red-600 hover:text-red-800"}`}
            title={confirmingDelete ? "Click again to confirm — this cannot be undone" : `Delete "${active.name}"`}
          >
            <Trash2 className="h-3.5 w-3.5" /> {confirmingDelete ? "Click to confirm" : "Delete this cadence"}
          </button>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200" />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={(active.steps || []).map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {(active.steps || []).map((s, i) => (
              <SortableStep
                key={s.id}
                step={s}
                idx={i}
                templates={templates}
                offers={offers}
                onChange={(patch) => updateStep(i, patch)}
                onRemove={() => removeStep(i)}
              />
            ))}
          </SortableContext>
        </DndContext>
        {(active.steps || []).length === 0 && (
          <div className="pl-14 text-sm text-slate-500 py-8 border border-dashed border-slate-200 rounded-md">
            <Sparkles className="h-4 w-4 inline mr-1 text-orange-500" />
            No steps yet in <b>{active.name}</b>. Click &ldquo;Add step&rdquo; to design the first touchpoint.
          </div>
        )}
      </div>

      {/* New cadence modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">New cadence</h3>
              <button onClick={() => setCreating(false)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Name</label>
                <input
                  value={newCadence.name}
                  onChange={(e) => setNewCadence({ ...newCadence, name: e.target.value })}
                  placeholder="e.g., VIP hot-lead cadence"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Kind</label>
                <select
                  value={newCadence.kind}
                  onChange={(e) => setNewCadence({ ...newCadence, kind: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                >
                  <option value="custom">Custom (manual / tag-driven)</option>
                  <option value="post_subscription">Post-Subscription</option>
                  <option value="win_back">Win-back</option>
                </select>
                <div className="text-[10px] text-slate-500 mt-1">{KIND_META[newCadence.kind]?.hint}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Description</label>
                <textarea
                  rows={3}
                  value={newCadence.description}
                  onChange={(e) => setNewCadence({ ...newCadence, description: e.target.value })}
                  placeholder="Short note for your team"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setCreating(false)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={createCadence} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150">
                <Save className="h-3.5 w-3.5" /> Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
